// ==========================================
// FILE: \src\context\WineContext.js
// ==========================================

import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  getDoc, 
  setDoc, 
  runTransaction, 
  writeBatch 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
// Importiamo il dizionario delle traduzioni statiche
import { translations } from '../utils/translations';

const WineContext = createContext({});

export const WineProvider = ({ children }) => {
  const [wines, setWines] = useState([]);
  const [locations, setLocations] = useState([]); // Le Cantine Fisiche 3D
  const [userProfile, setUserProfile] = useState(null); 
  const [isLoadingWines, setIsLoadingWines] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await fetchUserProfile(user.uid); 
        await fetchAllWines(user.uid);
        await fetchLocations(user.uid);   
      } else {
        setWines([]); 
        setLocations([]);
        setUserProfile(null);
        setIsLoadingWines(false);
      }
    });
    return unsubscribe;
  }, []);

  // --- MOTORE ENGINE DI TRADUZIONE LOCALE (Safe Fallback) ---
  const t = (keyPath, langOverride) => {
    // Se viene passato langOverride (es. dalla registrazione), usiamo quello, altrimenti il profilo o 'it'
    const currentLang = langOverride || userProfile?.language || 'it';
    
    // Separiamo le chiavi separate dal punto (es: 'global.save' -> ['global', 'save'])
    const keys = keyPath.split('.');
    
    // Cerchiamo la stringa nella lingua corrente
    let translation = translations[currentLang];
    for (const key of keys) {
      if (translation && translation[key] !== undefined) {
        translation = translation[key];
      } else {
        translation = null;
        break;
      }
    }

    // Se la traduzione esiste nella lingua corrente, la restituiamo
    if (translation && typeof translation === 'string') {
      return translation;
    }

    // FALLBACK 1: Se non esiste, cerchiamo la stessa chiave in Italiano ('it')
    let fallbackIt = translations['it'];
    for (const key of keys) {
      if (fallbackIt && fallbackIt[key] !== undefined) {
        fallbackIt = fallbackIt[key];
      } else {
        fallbackIt = null;
        break;
      }
    }
    if (fallbackIt && typeof fallbackIt === 'string') return fallbackIt;

    // FALLBACK 2: Se fallisce tutto, restituiamo il percorso della chiave stessa per non rompere l'UI
    return keyPath;
  };

  // --- CAMBIO LINGUA RUNTIME LATO PROFILO & FIRESTORE ---
  const changeLanguage = async (newLang) => {
    if (!userProfile) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
      const userRef = doc(db, 'users', uid);
      // Aggiornamento atomico sul Cloud Server
      await updateDoc(userRef, { language: newLang });
      // Mutazione immediata dello stato UI client-side
      setUserProfile(prev => (prev ? { ...prev, language: newLang } : prev));
    } catch (error) {
      console.error("Errore durante il salvataggio della lingua su Firestore:", error);
    }
  };

  // --- CARICAMENTO PROFILO UTENTE ---
  const fetchUserProfile = async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);
      const snap = await getDoc(userRef);
      
      if (snap.exists()) {
        let data = snap.data();
        let needsUpdate = false; 

        if (data.energia === undefined || data.energia === null || Number.isNaN(data.energia)) {
          data.energia = 5;
          data.isPremium = data.isPremium || false; 
          needsUpdate = true;
        }

        // Controllo e allineamento campo lingua per i profili legacy migrati
        if (data.language === undefined || data.language === null) {
          data.language = 'it';
          needsUpdate = true;
        }

        if (!data.isPremium && data.energia === 0) {
          const today = new Date().toDateString();
          const lastReset = data.lastEnergyReset ? new Date(data.lastEnergyReset).toDateString() : '';

          if (today !== lastReset) {
            data.energia = 1;
            data.lastEnergyReset = Date.now();
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          await updateDoc(userRef, { 
            energia: data.energia, 
            isPremium: data.isPremium,
            language: data.language,
            lastEnergyReset: data.lastEnergyReset || null 
          });
        }

        setUserProfile(data);
      } else {
        // CORREZIONE STRUTTURALE GDPR & CANCELLAZIONE: 
        // Se il profilo non esiste ancora su Firestore, NON dobbiamo forzare la creazione automatica di un 
        // record fittizio con consensi pre-accettati (true). Impostiamo lo stato locale a null e permettiamo 
        // alla LoginScreen di catturare lo stato, mostrando l'onboarding obbligatorio e la configurazione password.
        console.log("[VinoLogic Context] Nessun record trovato su Firestore per l'utente, attesa configurazione onboarding.");
        setUserProfile(null);
      }
    } catch (error) {
      console.error("Errore nel caricamento del profilo utente:", error);
    }
  };

  // --- TRANSAZIONE ATOMICA CONSUMO ENERGIA ---
  const consumeEnergy = async (cost = 1) => {
    if (!userProfile) return false;
    if (userProfile.isPremium) return true; 

    const uid = auth.currentUser?.uid;
    if (!uid) return false;

    try {
      const userRef = doc(db, 'users', uid);
      
      const newEnergyBalance = await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) throw new Error("Profilo utente inesistente.");

        const currentEnergy = userSnap.data().energia ?? 0;
        if (currentEnergy < cost) return false; 

        const finalEnergy = currentEnergy - cost;
        transaction.update(userRef, { energia: finalEnergy });
        return finalEnergy;
      });

      if (newEnergyBalance === false) return false;

      setUserProfile(prev => (prev ? { ...prev, energia: newEnergyBalance } : prev));
      return true;
    } catch (error) {
      console.error("Errore critico transazione con consumo energia:", error);
      return false;
    }
  };

  // --- TRANSAZIONE ATOMICA RICARICA ENERGIA ---
  const addEnergy = async (amount = 5) => {
    if (!userProfile) return;
    
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
      const userRef = doc(db, 'users', uid);

      const newEnergyBalance = await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) throw new Error("Profilo utente inesistente.");

        const currentEnergy = userSnap.data().energia ?? 0;
        const finalEnergy = currentEnergy + amount;
        transaction.update(userRef, { energia: finalEnergy });
        return finalEnergy;
      });

      if (newEnergyBalance !== null) {
        setUserProfile(prev => (prev ? { ...prev, energia: newEnergyBalance } : prev));
      }
    } catch (error) {
      console.error("Errore critical transazione con accredito energia:", error);
    }
  };

  // --- GESTIONE CATALOGO VINI ---
  const fetchAllWines = async (uid) => {
    setIsLoadingWines(true);
    try {
      const winesRef = collection(db, 'users', uid, 'wines');
      const snapshot = await getDocs(winesRef);
      
      const loadedWines = snapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          valueUnlocked: data.valueUnlocked ?? false,
          ownershipStatus: data.ownershipStatus || 'in_possesso',
          aromaticProfile: data.aromaticProfile || { tannino: 0, acidita: 0, corpo: 0, dolcezza: 0, note: [] }
        };
      });
      
      loadedWines.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setWines(loadedWines);
    } catch (error) {
      console.error("Errore nel caricamento della cantina:", error);
    } finally {
      setIsLoadingWines(false);
    }
  };

  const addWineLocal = async (wineData) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const docRef = await addDoc(collection(db, 'users', user.uid, 'wines'), wineData);
      const newWine = { id: docRef.id, ...wineData };
      setWines(prevWines => [newWine, ...prevWines]);
      return docRef.id;
    } catch (error) {
      throw error; 
    }
  };

  const updateWineLocal = async (id, updatedFields) => {
    const user = auth.currentUser;
    if (!user) return;
    const previousWines = [...wines];
    setWines(prevWines => prevWines.map(w => w.id === id ? { ...w, ...updatedFields } : w));
    try {
      const wineRef = doc(db, 'users', user.uid, 'wines', id);
      await updateDoc(wineRef, updatedFields);
    } catch (error) {
      setWines(previousWines);
      throw error; 
    }
  };

  // --- CANCELLAZIONE A CASCATA ---
  const deleteWineLocal = async (id) => {
    const user = auth.currentUser;
    if (!user) return;

    const previousWines = [...wines];
    const previousLocs = [...locations];

    setWines(prevWines => prevWines.filter(w => w.id !== id));
    setLocations(prevLocs => prevLocs.map(loc => {
      if (!loc.slots) return loc;
      return { ...loc, slots: loc.slots.filter(s => s.wineId !== id) };
    }));

    try {
      const batch = writeBatch(db);
      const wineRef = doc(db, 'users', user.uid, 'wines', id);
      batch.delete(wineRef);

      locations.forEach(loc => {
        if (loc.slots && loc.slots.some(s => s.wineId === id)) {
          const updatedSlots = loc.slots.filter(s => s.wineId !== id);
          const locRef = doc(db, 'users', user.uid, 'locations', loc.id);
          batch.update(locRef, { slots: updatedSlots });
        }
      });

      await batch.commit();
    } catch (error) {
      setWines(previousWines);
      setLocations(previousLocs);
      console.error("Errore durante la cancellazione a cascata:", error);
      throw error; 
    }
  };

  // --- GESTIONE CAVEAU FISICO (LOCATIONS) ---
  const fetchLocations = async (uid) => {
    try {
      const locRef = collection(db, 'users', uid, 'locations');
      const snapshot = await getDocs(locRef);
      const loadedLocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(loadedLocs);
    } catch (error) {
      console.error("Errore nel caricamento delle locations:", error);
    }
  };

  const addLocationLocal = async (locationData) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const finalData = { ...locationData, slots: locationData.slots || [], createdAt: Date.now() };
      const docRef = await addDoc(collection(db, 'users', user.uid, 'locations'), finalData);
      const newLoc = { id: docRef.id, ...finalData };
      setLocations(prev => [...prev, newLoc]);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  };

  const updateLocationLocal = async (id, updatedFields) => {
    const user = auth.currentUser;
    if (!user) return;
    const previousLocs = [...locations];
    setLocations(prev => prev.map(l => l.id === id ? { ...l, ...updatedFields } : l));
    try {
      const locRef = doc(db, 'users', user.uid, 'locations', id);
      await updateDoc(locRef, updatedFields);
    } catch (error) {
      setLocations(previousLocs);
      throw error;
    }
  };

  // --- SMANTELLAMENTO CANTINETTA ---
  const deleteLocationLocal = async (id) => {
    const user = auth.currentUser;
    if (!user) return;

    const previousLocs = [...locations];
    const previousWines = [...wines];
    const targetLoc = locations.find(l => l.id === id);
    const locName = targetLoc?.name;

    setLocations(prev => prev.filter(l => l.id !== id));
    if (locName) {
      setWines(prevWines => prevWines.map(w => w.location === locName ? { ...w, location: null } : w));
    }

    try {
      const batch = writeBatch(db);
      const locRef = doc(db, 'users', user.uid, 'locations', id);
      batch.delete(locRef);

      if (locName) {
        wines.forEach(wine => {
          if (wine.location === locName) {
            const wineRef = doc(db, 'users', user.uid, 'wines', wine.id);
            batch.update(wineRef, { location: null });
          }
        });
      }

      await batch.commit();
    } catch (error) {
      setLocations(previousLocs);
      setWines(previousWines);
      console.error("Errore durante lo smantellamento specchio della cantinetta:", error);
      throw error;
    }
  };

  // --- UTILITY: CALCOLO DEI VINI ORFANI ---
  const getUnplacedWines = () => {
    const presentWinesList = wines.filter(w => w.status === 'presente');
    const unplaced = [];

    presentWinesList.forEach(wine => {
      let placedCount = 0;
      locations.forEach(loc => {
        if (loc.slots) {
          placedCount += loc.slots.filter(slot => slot.wineId === wine.id).length;
        }
      });

      const missingToPlace = (wine.quantity || 1) - placedCount;
      if (missingToPlace > 0) {
        unplaced.push({ ...wine, missingToPlace });
      }
    });
    return unplaced;
  };

  const refreshWines = async () => {
    if (auth.currentUser) {
      await fetchUserProfile(auth.currentUser.uid);
      await fetchAllWines(auth.currentUser.uid);
      await fetchLocations(auth.currentUser.uid);
    }
  };

  return (
    <WineContext.Provider 
      value={{ 
        wines, 
        presentWines: wines.filter(w => w.status === 'presente'),
        pastWines: wines.filter(w => w.status === 'passato'),
        isLoadingWines, 
        addWineLocal, 
        updateWineLocal, 
        deleteWineLocal, 
        refreshWines,
        userProfile,
        consumeEnergy,
        addEnergy,
        locations,
        addLocationLocal,
        updateLocationLocal,
        deleteLocationLocal,
        getUnplacedWines,
        t,               // Esponiamo l'helper di traduzione ad ogni schermata dell'app
        changeLanguage   // Esponiamo la funzione per mutare lingua a runtime
      }}
    >
      {children}
    </WineContext.Provider>
  );
};

export const useWineContext = () => useContext(WineContext);